"use client";

import { ReactNode, useState } from "react";
import { useRouter } from "next/navigation";
import { Article, Course, Resume, Scribble } from "@/.content-collections/generated";
import { useClickOutside } from "@/hooks/use-click-outside";
import { Alert, Modal, ModalBody, ModalHeader, Tooltip } from "flowbite-react";
import { FaBook, FaUser } from "react-icons/fa6";
import { MdOutlineSchool } from "react-icons/md";
import { RiArticleLine, RiPagesLine } from "react-icons/ri";
import { TbScribble } from "react-icons/tb";

import { cn, toProperCase } from "@/lib/utils";
import { Bookmark } from "@/components/bookmark";
import { Comments } from "@/components/comments";
import { ImageContainer } from "@/components/image-container";
import { Like } from "@/components/like";
import { Link } from "@/components/link";
import { Markdown } from "@/components/markdown";
import { Metadata } from "@/components/metadata";
import { ShareWidget } from "@/components/share";
import { StickyHeader } from "@/components/sticky-header";
import { Summary } from "@/components/summary";
import { Tags } from "@/components/tags";

type IconProps = {
  icon: string;
  [key: string]: string;
};

const Icon = ({ icon, ...rest }: IconProps) => {
  switch (icon) {
    case "article":
      return <RiArticleLine {...rest} />;
    case "course":
      return <MdOutlineSchool {...rest} />;
    case "scribble":
      return <TbScribble {...rest} />;
    case "page":
      return <RiPagesLine {...rest} />;
    default:
      return <></>;
  }
};

type TypeProps = {
  type?: string;
  to: string;
  number?: number;
  inline?: boolean;
  featured?: boolean | null;
};

const Type = ({ type, to, number, inline, featured }: TypeProps) => {
  if (!type) return null;
  const title = toProperCase(type);
  return (
    <div
      className={cn(
        "whitespace-nowrap opacity-70",
        inline ? "text-left capitalize" : "my-3 text-center uppercase",
      )}
    >
      <Link href={to}>
        <Icon
          icon={type}
          className={cn("inline-block rounded text-4xl", inline ? "text-sm" : "p-2")}
          alt={title}
          title={title}
        />
        {featured && "Featured "}
        {type}
      </Link>
      {number ? <>&nbsp;&middot; #{number}</> : <> &middot;</>}
    </div>
  );
};

function PageStickyHeader({
  title,
  type,
  to,
  extra,
}: {
  number?: number;
  title: string;
  type: string;
  to: string;
  extra?: ReactNode;
}) {
  return (
    <StickyHeader>
      <div className="flex items-center gap-2 py-2">
        <Type type={type} to={to} inline={true} />
        <h1 className="text-sm! font-extrabold tracking-tighter dark:text-white">{title}</h1>
        {extra}
      </div>
    </StickyHeader>
  );
}

type NavigationItemProps = {
  title: string;
  slug?: string;
  number?: number;
};

function Navigation({
  previous,
  next,
}: {
  previous?: NavigationItemProps | null;
  next?: NavigationItemProps | null;
}) {
  if (!previous && !next) return;
  return (
    <nav className="mx-auto max-w-3xl print:hidden">
      {previous && previous.slug && (
        <div className="flex w-full justify-center text-center md:float-left md:w-1/2 md:justify-start md:text-left">
          <Tooltip content={previous.title} placement="bottom">
            <Link
              href={previous.slug}
              className="flex w-full items-center justify-center gap-2 text-sm hover:underline"
            >
              <span className="w-64 overflow-hidden text-ellipsis whitespace-nowrap">
                &larr; #{previous.number} - {previous.title}
              </span>
            </Link>
          </Tooltip>
        </div>
      )}
      {next && next.slug && (
        <div className="flex w-full justify-center md:float-right md:w-1/2 md:justify-end">
          <Tooltip content={next.title} placement="bottom">
            <Link
              href={next.slug}
              className="flex max-w-96 items-center gap-2 truncate overflow-hidden pt-2 text-sm text-ellipsis whitespace-nowrap hover:underline md:pt-0"
            >
              <span className="w-64 overflow-hidden text-center text-ellipsis whitespace-nowrap md:text-right">
                #{next.number} - {next.title}
              </span>{" "}
              <span>&rarr;</span>
            </Link>
          </Tooltip>
        </div>
      )}
      <div className="clear-both"></div>
    </nav>
  );
}

function Author() {
  return (
    <aside className="mx-auto mt-8 mb-6 flex max-w-3xl items-center not-italic">
      <div className="mr-3 inline-flex items-center text-sm text-gray-900 dark:text-white">
        <ImageContainer
          width={64}
          height={64}
          className="rounded-full border-4 border-white"
          containerClassName="mr-4"
          src="/headshot.webp"
          alt="Clarice Bouwer"
          priority={true}
          fill={false}
        />
        <div>
          <a href="/about" rel="author" className="text-lg font-bold text-gray-900 dark:text-white">
            Clarice Bouwer
          </a>
          <p className="text-base font-medium tracking-tight text-gray-500 dark:text-gray-400">
            Senior Software Engineer
          </p>
        </div>
      </div>
    </aside>
  );
}

function PageTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="mx-auto mb-3 max-w-sm text-center text-5xl font-extrabold tracking-tighter md:max-w-3xl lg:mb-6 lg:max-w-5xl lg:text-7xl dark:text-white print:text-black">
      {children}
    </h1>
  );
}

function TableOfContents({ course }: { course: Course }) {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));

  return (
    <div>
      <div
        role="button"
        className="flex cursor-pointer items-center gap-1 text-sm opacity-70"
        onClick={() => setIsOpen(true)}
      >
        <FaBook />
        Table of Contents
      </div>
      <Modal ref={modalRef} show={isOpen} size="lg" onClose={() => setIsOpen(false)}>
        <ModalHeader>Table of Contents</ModalHeader>
        <ModalBody className="max-h-96">
          {course.pages?.map((page, index) => (
            <div
              key={index}
              className="flex items-center justify-between border-b border-dashed py-2 hover:bg-pink-600"
            >
              <Link
                href={page?.slug}
                className="block w-full px-4 font-normal! text-black! hover:text-white! dark:text-white!"
                onClick={() => setIsOpen(false)}
              >
                {page && "number" in page && page.number}. {page?.title}
              </Link>
            </div>
          ))}
        </ModalBody>
      </Modal>
    </div>
  );
}

export function ArticleContent({
  article,
  bookmarks,
  likes,
}: {
  article: Article;
  bookmarks: string[];
  likes: string[];
}) {
  const {
    type,
    slug,
    number,
    title,
    tags,
    featured,
    timeToRead,
    date,
    abstract,
    summary,
    content,
    next,
    previous,
  } = article;
  return (
    <article className="format format-sm sm:format-base lg:format-lg format-blue dark:format-invert mx-auto w-full">
      <Navigation previous={previous} next={next} />
      <header className="mb-2 lg:mb-4">
        <Type type={type} to="/blog" number={number} featured={featured} />
        <PageTitle>{title}</PageTitle>
        <div className="text-center">
          <Metadata timeToRead={timeToRead} date={date} type={type} />
        </div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <Bookmark bookmarks={bookmarks} slug={slug} />
          <Like likes={likes} slug={slug} />
        </div>
        <div className="text-center">
          {tags && <Tags tags={tags} redirect={true} isButton={true} />}
        </div>
        <Author />
      </header>
      <PageStickyHeader number={number} title={title} type={type} to="/blog" />
      <section id="article" className="mx-auto mb-8 max-w-3xl">
        <Markdown content={content} />
      </section>
      <ShareWidget title={title} url={slug} />
      <Navigation previous={previous} next={next} />
      <Comments />
      <Summary summary={summary ?? abstract} text={content} />
    </article>
  );
}

export function ResumeContent({ resumePage }: { resumePage: Resume }) {
  if (!resumePage) return null;
  const { resume } = resumePage;
  return (
    <article>
      <header className="mb-2 lg:mb-4">
        {resume.logo && (
          <ImageContainer
            width={112}
            height={112}
            src={`/logos/${resume.logo}`}
            alt={resume.company ?? (resume.name || "Logo")}
            className="rounded-md bg-white p-2"
            containerClassName="mx-auto"
            priority={true}
            fill={false}
          />
        )}
        <PageTitle>{resume.company ?? resume.name}</PageTitle>
      </header>
      <PageStickyHeader title={resume.company ?? ""} type="resume" to="/resume" />
      <section id="article" className="mx-auto mb-8 max-w-3xl">
        <Markdown content={resumePage.content} />
      </section>
    </article>
  );
}

export function ScribbleContent({
  scribble,
  bookmarks,
  likes,
}: {
  scribble: Scribble;
  bookmarks: string[];
  likes: string[];
}) {
  const { type, slug, number, title, tags, timeToRead, date, content, devTo, next, previous } =
    scribble;
  return (
    <article className="format format-sm sm:format-base lg:format-lg format-blue dark:format-invert mx-auto w-full">
      <Navigation previous={previous} next={next} />
      <header className="mb-2 lg:mb-4">
        <Type type={type} to="/scribbles" number={number} />
        <PageTitle>{title}</PageTitle>
        <div className="text-center">
          <Metadata timeToRead={timeToRead} date={date} type={type} link={devTo} />
        </div>
        <div className="mt-2 flex items-center justify-center gap-2">
          <Bookmark bookmarks={bookmarks} slug={slug} />
          <Like likes={likes} slug={slug} />
        </div>
        <div className="text-center">
          {tags && <Tags tags={tags} redirect={true} isButton={true} />}
        </div>
        <Author />
      </header>
      <PageStickyHeader number={number} title={title} type={type} to="/scribbles" />
      <section id="article" className="mx-auto mb-8 max-w-3xl">
        <Markdown content={content} />
      </section>
      <ShareWidget title={title} url={slug} />
      <Navigation previous={previous} next={next} />
      <Comments />
    </article>
  );
}

export function CourseContent({
  course,
  bookmarks,
  likes,
  isAuthenticated,
}: {
  course: Course;
  bookmarks: string[];
  likes: string[];
  isAuthenticated: boolean;
}) {
  const {
    type,
    slug,
    number,
    title,
    tags,
    timeToRead,
    date,
    modified,
    content,
    pages,
    next,
    previous,
  } = course;
  const router = useRouter();
  return (
    <article className="format format-sm sm:format-base lg:format-lg format-blue dark:format-invert mx-auto w-full">
      <Navigation previous={previous} next={next} />
      <header className="mb-2 lg:mb-4">
        <Type type={type} to="/courses" number={number} />
        <PageTitle>{title}</PageTitle>
        <div className="text-center">
          <Metadata
            timeToRead={timeToRead}
            date={date}
            type={type}
            modified={modified ? new Date(modified) : undefined}
          />
        </div>
        {slug && (
          <div className="mt-2 flex items-center justify-center gap-2">
            <Bookmark bookmarks={bookmarks} slug={slug} />
            <Like likes={likes} slug={slug} />
          </div>
        )}
        <div className="text-center">
          {tags && <Tags tags={tags} redirect={true} isButton={true} />}
        </div>
        <Author />
      </header>
      {number && title && type && (
        <PageStickyHeader number={number} title={title} type={type} to="/courses" />
      )}
      <section id="article" className="mx-auto mb-8 max-w-3xl">
        {content && <Markdown content={content} />}
        <nav>
          <h2>Pages</h2>
          {!isAuthenticated && (
            <Alert
              color="red"
              className="w-full border"
              additionalContent={
                <div className="flex items-center gap-2">
                  <button
                    className="cursor-pointer rounded-md bg-black px-4 py-1 text-white hover:bg-pink-500"
                    onClick={() => {
                      const returnTo = encodeURIComponent(course.slug);
                      router.push(`/handler/sign-in?after_auth_return_to=${returnTo}`);
                    }}
                  >
                    Sign in
                  </button>
                  or
                  <button
                    className="cursor-pointer rounded-md bg-black px-4 py-1 text-white hover:bg-pink-500"
                    onClick={() => {
                      const returnTo = encodeURIComponent(course.slug);
                      router.push(`/handler/sign-up?after_auth_return_to=${returnTo}`);
                    }}
                  >
                    Sign up
                  </button>
                  for free access.
                </div>
              }
            >
              <div className="flex items-center justify-start gap-2">
                <FaUser className="size-4" />
                You need to be signed in to view the course material.
              </div>
            </Alert>
          )}
          {pages?.map(
            (page, index) =>
              page && (
                <div
                  key={index}
                  className="flex items-center justify-between border-b border-dashed px-4 py-4 hover:bg-pink-600"
                >
                  <Link
                    href={page.slug}
                    className="block w-full font-normal! text-black! hover:text-white! dark:text-white!"
                  >
                    {"number" in page && page.number}. {page.title}
                  </Link>
                  <Bookmark bookmarks={bookmarks} slug={slug} />
                </div>
              ),
          )}
        </nav>
      </section>
      {title && slug && <ShareWidget title={title} url={slug} />}
      <Navigation previous={previous} next={next} />
      <Comments />
    </article>
  );
}

export function CoursePageContent({
  coursePage,
  course,
  bookmarks,
  likes,
}: {
  coursePage: Course;
  course: Course;
  bookmarks: string[];
  likes: string[];
}) {
  const {
    type,
    slug,
    number,
    title,
    tags,
    timeToRead,
    date,
    modified,
    summary,
    abstract,
    content,
    next,
    previous,
  } = coursePage;
  return (
    <article className="format format-sm sm:format-base lg:format-lg format-blue dark:format-invert mx-auto w-full">
      <Navigation previous={previous} next={next} />
      {number && title && type && (
        <PageStickyHeader
          number={number}
          title={title}
          type={type}
          to={course?.slug}
          extra={<TableOfContents course={course} />}
        />
      )}
      <header className="mb-2 lg:mb-4">
        <Type type={type} to={course?.slug} number={number} />
        <PageTitle>{title}</PageTitle>
        <div className="text-center">
          {course && (
            <div className="mb-2 flex items-center justify-center gap-2">
              <Link href={course.slug} className="block">
                {course.title}
              </Link>
              <TableOfContents course={course} />
            </div>
          )}
          <Metadata
            timeToRead={timeToRead}
            date={date}
            type={type}
            modified={modified ? new Date(modified) : undefined}
          />
        </div>
        {slug && (
          <div className="mt-2 flex items-center justify-center gap-2">
            <Bookmark bookmarks={bookmarks} slug={slug} />
            <Like likes={likes} slug={slug} />
          </div>
        )}
        <div className="text-center">
          {tags && <Tags tags={tags} redirect={true} isButton={true} />}
        </div>
        <Author />
      </header>
      <section id="article" className="mx-auto mb-8 max-w-3xl">
        {content && <Markdown content={content} />}
      </section>
      {title && slug && <ShareWidget title={title} url={slug} />}
      <Navigation previous={previous} next={next} />
      <Comments />
      <Summary summary={summary ?? abstract} text={content} />
    </article>
  );
}
